/**
 * Backend Health Check Component
 * Displays connection status and provides retry functionality
 */

import { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle2, Wifi, WifiOff, RefreshCw, Lightbulb } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { apiClient } from '../../lib/api-client';

interface BackendHealthCheckProps {
  /**
   * Error that occurred during API call
   */
  error: Error | string | null;
  
  /**
   * Function to retry the connection
   */
  onRetry: () => void;
  
  /**
   * Whether to show full error page or just a banner
   * @default 'page'
   */
  display?: 'page' | 'banner';
  
  /**
   * Backend URL to display
   * @default 'http://172.235.19.18:8004/api/v1'
   */
  backendUrl?: string;
}

export function BackendHealthCheck({ 
  error, 
  onRetry, 
  display = 'page',
  backendUrl = 'http://172.235.19.18:8004/api/v1'
}: BackendHealthCheckProps) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [backendStatus, setBackendStatus] = useState<'checking' | 'online' | 'offline'>('checking');

  const errorMessage = typeof error === 'string' ? error : error?.message || 'Unknown error';
  const isConnectionError = errorMessage.includes('Cannot connect to server') || 
                           errorMessage.includes('Failed to fetch') ||
                           errorMessage.includes('Network request failed');

  useEffect(() => {
    // Check backend status periodically
    const checkStatus = async () => {
      try {
        const response = await fetch(`${backendUrl}/docs`, { method: 'HEAD' });
        setBackendStatus(response.ok ? 'online' : 'offline');
      } catch {
        setBackendStatus('offline');
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [backendUrl]);

  const handleRetry = async () => {
    setIsRetrying(true);
    await new Promise(resolve => setTimeout(resolve, 500)); // Small delay for UX
    onRetry();
    setIsRetrying(false);
  };

  if (display === 'banner') {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
        <div className="flex items-start gap-3">
          <WifiOff className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800">Backend Connection Error</h3>
            <p className="text-sm text-red-700 mt-1">{errorMessage}</p>
            <div className="mt-3 flex items-center gap-2">
              <Button 
                size="sm"
                onClick={handleRetry}
                disabled={isRetrying}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {isRetrying ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Retry
                  </>
                )}
              </Button>
              <a 
                href={`${backendUrl}/docs`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-red-700 underline hover:text-red-800"
              >
                Check Backend Status
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Full page error display
  return (
    <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
      <Card className="max-w-2xl w-full shadow-lg">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-6 h-6" />
              Backend Connection Error
            </CardTitle>
            <Badge 
              variant={backendStatus === 'online' ? 'default' : 'destructive'}
              className="flex items-center gap-1"
            >
              {backendStatus === 'checking' ? (
                <>
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Checking...
                </>
              ) : backendStatus === 'online' ? (
                <>
                  <CheckCircle2 className="w-3 h-3" />
                  Online
                </>
              ) : (
                <>
                  <WifiOff className="w-3 h-3" />
                  Offline
                </>
              )}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6 pt-6">
          {/* Error Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800 font-medium mb-1">Error Details:</p>
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>

          {/* Backend Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-900 mb-2">Backend Server:</p>
            <code className="text-xs bg-blue-100 px-2 py-1 rounded block break-all">
              {backendUrl}
            </code>
          </div>

          {/* Troubleshooting Steps */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="font-medium text-yellow-800 mb-3 flex items-center gap-2">
              <Lightbulb className="w-4 h-4" />
              Troubleshooting Steps:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-sm text-yellow-700">
              <li>
                <strong>Check if backend server is running</strong>
                <div className="ml-6 mt-1 bg-yellow-100 p-2 rounded text-xs font-mono">
                  ssh root@172.235.19.18<br />
                  cd /path/to/backend<br />
                  uvicorn app.main:app --host 0.0.0.0 --port 8004 --reload
                </div>
              </li>
              <li>
                <strong>Verify network connectivity</strong>
                <div className="ml-6 mt-1 bg-yellow-100 p-2 rounded text-xs font-mono">
                  ping 172.235.19.18<br />
                  curl http://172.235.19.18:8004/api/v1/docs
                </div>
              </li>
              <li>
                <strong>Check firewall settings</strong>
                <p className="ml-6 mt-1 text-xs">Ensure port 8004 is open and accessible</p>
              </li>
              <li>
                <strong>Review backend logs</strong>
                <p className="ml-6 mt-1 text-xs">Check for any startup errors or crashes</p>
              </li>
            </ol>
          </div>

          {/* Quick Actions */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="font-medium text-gray-800 mb-3">Quick Actions:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a 
                href={`${backendUrl}/docs`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                <Wifi className="w-4 h-4" />
                Open API Docs
              </a>
              <a 
                href={`${backendUrl}/redoc`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                <Wifi className="w-4 h-4" />
                Open ReDoc
              </a>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button 
              onClick={handleRetry}
              disabled={isRetrying}
              className="flex-1 bg-[#2762AC] hover:bg-[#1d4a7f]"
            >
              {isRetrying ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Retrying Connection...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry Connection
                </>
              )}
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.location.reload()}
              className="flex-1"
            >
              Reload Page
            </Button>
          </div>

          {/* Help Text */}
          <div className="text-center text-xs text-gray-500 pt-2 border-t">
            <p>If the problem persists, contact your system administrator.</p>
            <p className="mt-1">
              Documentation: <code className="bg-gray-100 px-1 rounded">/CHECK_BACKEND_CONNECTION.md</code>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Export a hook to easily use this in other components
export function useBackendHealth(backendUrl = 'http://localhost:8004/api/v1') {
  const [status, setStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const checkHealth = async () => {
    try {
      const response = await fetch(`${backendUrl}/docs`, { 
        method: 'HEAD',
        cache: 'no-cache' 
      });
      setStatus(response.ok ? 'online' : 'offline');
      setLastChecked(new Date());
      return response.ok;
    } catch {
      setStatus('offline');
      setLastChecked(new Date());
      return false;
    }
  };

  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [backendUrl]);

  return { status, lastChecked, checkHealth };
}
